import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react';
import { pcServiceUrl } from '../config';
import { prepareAttributes } from '../hooks/car-model-helper';
import carMakesTranslate from '../assets/translations/car-makes';

const CarsContext = createContext();

const initialState = {
  cars: [],
  filteredCars: [],
  isLoading: true,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        cars: action.payload,
        filteredCars: action.payload,
        isLoading: false,
        error: null,
      };
    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    case 'SET_FILTERED_CARS':
      return { ...state, filteredCars: action.payload };
    default:
      throw new Error('Unknown action type: ' + action.type);
  }
}

function CarsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchCars() {
      try {
        dispatch({ type: 'FETCH_START' });

        const res = await fetch(`${pcServiceUrl}/api/v1/car/search`, {
          signal: controller.signal,
          method: 'GET',
        });

        if (!res.ok) {
          throw new Error(
            res.status === 404 ? 'Cars not found' : 'Failed to fetch cars'
          );
        }

        const data = await res.json();
        //console.log('API Response cars:', data.cars);
        const carResults = Object.values(data.cars).map((car) => {
          const attributes = prepareAttributes(car);
          let pictures = [];

          if (car?.pictures) {
            pictures = Object.entries(car.pictures)
              .map(([id, { name, url }]) => ({ id, name, image: url }))
              .filter((picture) => picture.id !== 'og')
              .sort((a, b) => {
                if (a.id === 'showcase') return -1;
                if (b.id === 'showcase') return 1;
                return 0;
              });
          }

          // console.log('Pictures:', pictures);

          const result = {
            ...car,
            make: carMakesTranslate[car.make] ?? car.make,
            attributes,
            pictures,
            dealerId: car.sellerId, // Use sellerId instead of dealerId
          };
          //console.log('Processed car:', result);
          return result;
        });

        //console.log('Car Results:', carResults);

        dispatch({ type: 'FETCH_SUCCESS', payload: carResults });
      } catch (err) {
        console.error('Error fetching cars:', err);
        dispatch({ type: 'FETCH_ERROR', payload: err.message });
      }
    }

    fetchCars();

    return () => controller.abort();
  }, []);

  // Memoize filterCars function
  const filterCars = useCallback(
    (filters) => {
      if (!filters || Object.keys(filters).length === 0) {
        dispatch({ type: 'SET_FILTERED_CARS', payload: state.cars });
        return;
      }

      const filteredCars = state.cars.filter((car) => {
        // Filter by make
        if (
          filters.makes?.length &&
          !filters.makes.some(
            (make) => car.make?.toLowerCase() === make.name?.toLowerCase()
          )
        ) {
          return false;
        }

        // Filter by model
        if (
          filters.models?.length &&
          !filters.models.some(
            (model) => car.model?.toLowerCase() === model.name?.toLowerCase()
          )
        ) {
          return false;
        }

        // Filter by year
        if (
          filters.years?.length &&
          !filters.years.some(
            (year) =>
              year.name === 'All Years' ||
              car.firstRegistration?.toString() === year.name?.toString()
          )
        ) {
          return false;
        }

        // Filter by body style
        if (
          filters.bodys?.length &&
          !filters.bodys.some(
            (body) => car.bodyStyle?.toLowerCase() === body.name?.toLowerCase()
          )
        ) {
          return false;
        }

        // Filter by transmission
        if (
          filters.transmissions?.length &&
          !filters.transmissions.some(
            (transmission) =>
              car.transmission?.toLowerCase() ===
              transmission.name?.toLowerCase()
          )
        ) {
          return false;
        }

        // Filter by price range
        if (filters.prices?.length) {
          const priceRange = filters.prices[0];
          const carPrice = parseFloat(car.price);
          if (carPrice < priceRange.min || carPrice > priceRange.max) {
            return false;
          }
        }

        return true;
      });

      dispatch({ type: 'SET_FILTERED_CARS', payload: filteredCars });
    },
    [state.cars]
  ); // Only recreate when cars array changes

  const value = {
    ...state,
    filterCars,
  };

  return <CarsContext.Provider value={value}>{children}</CarsContext.Provider>;
}

function useCars() {
  const context = useContext(CarsContext);
  if (context === undefined) {
    throw new Error('useCars must be used within a CarsProvider');
  }
  return context;
}

export { CarsProvider, useCars };
